import apiClient, { getApiErrorMessage } from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    ConfigTemplateActiveStatus,
    ConfigTemplateApplyResult,
    ConfigTemplateCreateBody,
    ConfigTemplateDocument,
    ConfigTemplateFieldSchemaResponse,
    ConfigTemplateLevel,
    ConfigTemplateListRequest,
    ConfigTemplateListResponse,
    ConfigTemplateOverlayKeys,
    ConfigTemplateTagsRequest,
    ConfigTemplateTargetBrand,
    ConfigTemplateTargetFilters,
    ConfigTemplateTargetListResponse,
    ConfigTemplateTargetStore,
    ConfigTemplateUpdateBody,
} from '../types/configTemplate.types';

type Envelope<T> = { status: string; message?: string; data: T };

const CT = API_ENDPOINTS.CONFIG_TEMPLATE;

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

export function parseConfigTemplateError(err: unknown): {
    message: string;
    statusCode?: number;
    clashName?: string;
    isNameClash: boolean;
} {
    const root = asRecord(err);
    const payload = asRecord(root?.data);
    const nested = asRecord(payload?.data);
    const message = (
        (typeof payload?.message === 'string' && payload.message.trim()) ||
        getApiErrorMessage(err)
    ).trim();
    const statusCode =
        typeof root?.status === 'number'
            ? root.status
            : typeof root?.statusCode === 'number'
              ? root.statusCode
              : undefined;
    const clashName =
        (typeof nested?.name === 'string' && nested.name.trim()) ||
        (typeof payload?.name === 'string' && payload.name.trim()) ||
        undefined;
    const isNameClash = statusCode === 409 || /already exists/i.test(message);
    return { message, statusCode, clashName, isNameClash };
}

export async function getFieldSchema(body: {
    level: ConfigTemplateLevel;
}): Promise<ConfigTemplateFieldSchemaResponse> {
    const response = await apiClient.post<Envelope<ConfigTemplateFieldSchemaResponse>>(
        CT.FIELD_SCHEMA,
        body
    );
    return response.data;
}

export async function listConfigTemplates(
    body: ConfigTemplateListRequest
): Promise<ConfigTemplateListResponse> {
    const response = await apiClient.post<Envelope<ConfigTemplateListResponse>>(CT.LIST, {
        limit: 100,
        ...body,
        level: body.level,
    });
    return response.data;
}

export async function listConfigTemplateTags(body: ConfigTemplateTagsRequest): Promise<string[]> {
    const response = await apiClient.post<Envelope<{ tags: string[] }>>(CT.TAGS, body);
    return response.data.tags ?? [];
}

export async function createConfigTemplate(
    body: ConfigTemplateCreateBody
): Promise<ConfigTemplateDocument> {
    const response = await apiClient.post<Envelope<ConfigTemplateDocument>>(CT.CREATE, body);
    return response.data;
}

export async function getConfigTemplateDetails(body: {
    id: string;
}): Promise<ConfigTemplateDocument> {
    const response = await apiClient.post<Envelope<ConfigTemplateDocument>>(CT.DETAILS, body);
    return response.data;
}

export async function updateConfigTemplate(
    body: ConfigTemplateUpdateBody
): Promise<ConfigTemplateDocument> {
    const response = await apiClient.patch<Envelope<ConfigTemplateDocument>>(CT.UPDATE, body);
    return response.data;
}

export async function updateConfigTemplateStatus(body: {
    id: string;
    activeStatus: ConfigTemplateActiveStatus;
}): Promise<ConfigTemplateDocument> {
    const response = await apiClient.patch<Envelope<ConfigTemplateDocument>>(CT.STATUS, body);
    return response.data;
}

export async function listStoreBrandTargets(
    filters: ConfigTemplateTargetFilters
): Promise<ConfigTemplateTargetListResponse<ConfigTemplateTargetBrand>> {
    const response = await apiClient.post<
        Envelope<ConfigTemplateTargetListResponse<ConfigTemplateTargetBrand>>
    >(CT.STORE_BRANDS_LIST, { limit: 25, ...filters });
    return response.data;
}

export async function listStoreTargets(
    filters: ConfigTemplateTargetFilters
): Promise<ConfigTemplateTargetListResponse<ConfigTemplateTargetStore>> {
    const response = await apiClient.post<
        Envelope<ConfigTemplateTargetListResponse<ConfigTemplateTargetStore>>
    >(CT.STORES_LIST, { limit: 25, ...filters });
    return response.data;
}

export async function copyConfigTemplateFromLive(body: {
    level: ConfigTemplateLevel;
    storeBrandId?: string;
    storeId?: string;
    templateId?: string;
}): Promise<
    Pick<ConfigTemplateDocument, 'storeBrandConfig' | 'storeBrandSettings' | 'storeConfig'>
> {
    const response = await apiClient.post<
        Envelope<
            Pick<ConfigTemplateDocument, 'storeBrandConfig' | 'storeBrandSettings' | 'storeConfig'>
        >
    >(CT.COPY_FROM_LIVE, body);
    return response.data;
}

export async function getConfigTemplateOverlayKeys(body: {
    id: string;
}): Promise<ConfigTemplateOverlayKeys> {
    const response = await apiClient.post<Envelope<ConfigTemplateOverlayKeys>>(
        CT.OVERLAY_KEYS,
        body
    );
    return response.data;
}

export async function applyConfigTemplate(body: {
    id: string;
    storeBrandIds?: string[];
    storeIds?: string[];
    overrideStateVisibility?: boolean;
}): Promise<ConfigTemplateApplyResult> {
    const response = await apiClient.post<Envelope<ConfigTemplateApplyResult>>(CT.APPLY, body, {
        timeout: 120_000,
    });
    return response.data;
}
